import { Typography, TypographyProps } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { useState } from 'react';

import { Icon } from '@eventbrite/legacy-eds.icon';
import { HelpChunky } from '@eventbrite/legacy-eds.iconography';
import { withTooltip } from '@eventbrite/legacy-eds.tooltip';

import styles from './EventCardPromoted.module.scss';
import { PromotedUpsellModal } from './PromotedUpsellModal';
import { useIntl } from 'react-intl';

interface EventCardPromotedProps extends Omit<TypographyProps, 'children'> {
    isPromoted?: boolean;
    label?: string;
    withUpsellModal?: boolean;
    icon?: React.ReactNode;
}

const IconWithTooltip = withTooltip(Icon);

/**
 * Migrated from eb-ui on 2025/05/29. [c2c7c3a](https://github.com/eventbrite/eb-ui/blob/c2c7c3a8cbc538f6e6902f06be75ed8ab5c66511/packages/attendee/event-renderer/src/components/EventCardPromoted/EventCardPromoted.tsx)
 */
const EventCardPromotedBase: React.FunctionComponent<EventCardPromotedProps> = ({
    isPromoted,
    className,
    withUpsellModal,
    label,
    icon,
    ...typographyProps
}) => {
    const [showModal, setShowModal] = useState(false);
    const openModal = () => setShowModal(true);
    const { formatMessage } = useIntl();

    if (!isPromoted) {
        return null;
    }

    if (!label) {
        label = formatMessage({ id: 'Promoted', defaultMessage: 'Promoted' });
    }

    // Use the provided icon or default to HelpChunky
    const iconToRender = icon || <HelpChunky />;

    return (
        <>
            <div className={classNames(className, styles.root)}>
                <Typography
                    variant="body-sm"
                    color="neutral-600"
                    {...typographyProps}
                    className={classNames(withUpsellModal && styles.withUpsellModal)}
                    onClick={withUpsellModal ? openModal : undefined}
                    role={withUpsellModal ? 'button' : undefined}
                >
                    {label}
                </Typography>
                <span className={styles.iconWrapper} tabIndex={0}>
                    <IconWithTooltip
                        type={iconToRender}
                        aria-label={formatMessage({
                            id: 'Promoted event listing',
                            defaultMessage: 'Promoted event listing',
                        })}
                        size="xsmall"
                        tooltipId="tooltip-info"
                        tooltipStyle="phab"
                        wrapperComponent="span"
                        enhancedA11y={true}
                        tooltipText={
                            <Typography variant="body-sm">
                                {formatMessage({
                                    id: 'Event organizers pay for these premium spots so they can reach people searching for similar things to do.',
                                    defaultMessage:
                                        'Event organizers pay for these premium spots so they can reach people searching for similar things to do.',
                                })}
                            </Typography>
                        }
                        tooltipArrowLocation="left"
                    />
                </span>
            </div>
            {showModal && <PromotedUpsellModal handleClose={() => setShowModal(false)} />}
        </>
    );
};

//Memoize result to prevent needing to re-render
//withTooltip every instance unless necessary.
export const EventCardPromoted = React.memo(
    EventCardPromotedBase,
    (prevProps, nextProps) => prevProps.isPromoted === nextProps.isPromoted,
);
